<template>
    <BasePanel
        headline="lang_ERP_PRICELISTS_CAPTION_PRICELISTS_ROWS"
        :alvqa="alvqa"
    >
        <template v-slot:panelContent>
            <v-data-table
                class="baseInlineEditingDataTable"
                :headers="headers"
                :items="items"
                :items-per-page="itemsPerPage"
                :header-props="{ sortIcon: '' }"
                hide-default-footer   
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"     
            >
                <template v-slot:top>
                    <v-row class="pb-3">
                        <v-spacer></v-spacer>
                        <v-col cols="2">
                            <BaseButton 
                                @click="addNewRow"
                                :disabled="$route.params.alvId === 'new'"
                                label="lang_GENERAL_BUTTON_ADD_ROW"
                                block
                                :alvqa="alvqa + '-button-add-row'"
                            />
                        </v-col>
                    </v-row>
                </template>

                <!-- Headers -->
                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>

                <!-- Distributor name -->
                <template v-slot:[`item.distributor`]="{ item }">
                    <BaseTooltip>
                        <template v-slot:text>{{ item.distributor.alvId }}</template>
                        <template v-slot:content>
                            <div class="ellipsis" :alvqa="alvqa + '-edit-row-' + item.distributor.alvId + '-distributor'">
                                {{ getGroupName(item) }}
                            </div>
                        </template>
                    </BaseTooltip>
                </template>

                <!-- Regions -->
                <template v-slot:[`item.regions`]="{ item }">
                    <div :alvqa="alvqa + '-edit-row-' + item.id + '-regions'">
                        {{ convertRegionsToString(item.regions) }}
                    </div>
                </template>

                <!-- MSP manufacturer suggested price aka VAP -->
                <template v-slot:[`item.msp`]="{ item }">
                    <i18n-n v-if="item.msp != null" :value="$alvPlugin.stringToNumber(item.msp)" format="number"></i18n-n>
                </template>   

                <!-- PPD published price to dealer aka HAP -->
                <template v-slot:[`item.ppd`]="{ item }">
                    <i18n-n v-if="item.ppd != null" :value="$alvPlugin.stringToNumber(item.ppd)" format="number"></i18n-n>
                </template>

                <!-- SRP suggested retail price aka EKP -->
                <template v-slot:[`item.srp`]="{ item }">
                    <i18n-n v-if="item.srp != null" :value="$alvPlugin.stringToNumber(item.srp)" format="number"></i18n-n>
                </template>  

                <!-- SPS salesprice published to society aka TAS-HAP -->
                <template v-slot:[`item.sps`]="{ item }">
                    <i18n-n v-if="item.sps != null" :value="$alvPlugin.stringToNumber(item.sps)" format="number"></i18n-n>
                </template>   

                <!-- Price category -->
                <template v-slot:[`item.priceCategory`]="{ item }">
                    <div :alvqa="alvqa + '-edit-row-' + item.id + '-price-category'">
                        {{ $t(getPriceCategory(item.priceCategory)) }}
                    </div>
                </template>

                <!-- Currency -->
                <template v-slot:[`item.currency`]="{ item }">
                    <div :alvqa="alvqa + '-edit-row-' + item.id + '-currency'">
                        {{ item.currency }}
                    </div>
                </template>

                <!-- Buttons -->
                <template v-slot:[`item.delete`]="{ item }">
                    <BaseIcon 
                        @click="deleteRow(item)"
                        icon="delete"
                        color="iconDelete"
                        tooltipText="lang_GENERIC_CAPTION_DELETE"
                        :alvqa="alvqa + 'edit-row-' + item.id + '-icon-delete'"
                    />
                </template>
                <template v-slot:[`item.inlineEdit`]="{ item }">
                    <BaseIcon   
                        :alvqa="alvqa + '-edit-row-' + item.id + '-icon-inline-edit'"
                        icon="edit" 
                        tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"   
                        @click.stop="editInlineFunction(item)"
                    />
                </template>
            </v-data-table>
        </template>
    </BasePanel>
</template>

<script>

import BasePanel from '@/components/_base/BasePanel'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'
import BaseTooltip from '@/components/_base/BaseTooltip'
import { DropdownGeneralPriceCategories } from '@/assets/dropdowns/alvDropdowns.js'

export default {
    name: "PricelistsRowsEditPanel",
    components: {
        BasePanel,
        BaseButton,
        BaseIcon,
        BaseTooltip
    },
    data() {
        return {
            alvqa: 'erp-pricelists-edit-form-panel-pricelists-rows'
        }
    },
    computed: {
        items() {
            return this.$store.getters.getPricelistsPricelistsRows
        },
        headers() {
            return [
                {   text: this.$t('lang_ERP_PRICELISTS_CAPTION_DISTRIBUTOR'), 
                    alvqa: this.alvqa + "header-distributor",
                    align: "left", 
                    sortable: false, 
                    value: "distributor", 
                    tooltip:"", 
                    class: 'text-no-wrap'},

                {   text: this.$t('lang_ERP_PRICELISTS_CAPTION_REGION'), 
                    alvqa: this.alvqa + "header-region",
                    align: "left", 
                    sortable: false, 
                    value: "regions",
                    tooltip:"", 
                    width: "120px"},
                    
                {   text: this.$t('lang_GENERIC_LABEL_CAPTION_MSP'), 
                    alvqa: this.alvqa + "header-msp-manufacturer-sales-price",
                    align: "right", 
                    sortable: false, 
                    value: "msp", 
                    tooltip:"", 
                    width: "80px"},
                    
                {   text: this.$t('lang_GENERIC_LABEL_CAPTION_PPD'), 
                    alvqa: this.alvqa + "header-ppd-published-price-to-dealer",
                    align: "right", 
                    sortable: false, 
                    value: "ppd", 
                    tooltip:"", 
                    width: "80px"},

                {   text: this.$t('lang_GENERIC_LABEL_CAPTION_SRP'), 
                    alvqa: this.alvqa + "header-srp-suggested-retail-price",
                    align: "right", 
                    sortable: false, 
                    value: "srp", 
                    tooltip:"", 
                    width: "80px"},
                    
                {   text: this.$t('lang_GENERIC_LABEL_CAPTION_SPS'), 
                    alvqa: this.alvqa + "header-sps-salesprice-published-to-society",
                    align: "right", 
                    sortable: false, 
                    value: "sps", 
                    tooltip:"", 
                    width: "80px"},

                {   text: this.$t('lang_ERP_PRICELISTS_CAPTION_PRICE_CATEGORY'), 
                    alvqa: this.alvqa + "header-price-category",
                    align: "left", 
                    sortable: false, 
                    value: "priceCategory", 
                    tooltip:"", 
                    width: "120px"},

                {   text: this.$t('lang_ACCOUNT_COMPANIES_CAPTION_CURRENCY'), 
                    alvqa: this.alvqa + "header-currency",
                    align: "left", 
                    sortable: false, 
                    value: "currency", 
                    tooltip:"", 
                    width: "120px"},

                {
                    text: '', 
                    value: 'inlineEdit',
                    align: 'right',
                    sortable: false,
                    width: "70px",
                    class: 'text-no-wrap'
                },
                
                {
                    text: "",
                    align: "right",
                    width: "70px",
                    value: "delete",
                    sortable: false,
                    class: 'text-no-wrap'
                }
            ]
        },
        itemsPerPage() { return this.$store.getters.getItemsPerPage },
    },
    methods: {

        // Regions are coming back as objects with .include and .exclude from the BE
        // but in FE we only handle strings with + and -, so when EditPanel list is created
        // all regions must be converted to strings, also because strings are used in EditDialog
        convertRegionsToString(regions) {
            if (regions != null) {
                if (regions.include) {
                    return this.$alvPlugin.createRegionsToString(regions)
                } else {
                    return regions
                }
            }
        },

        getGroupName(item) {
            if (item.distributor.group)
                return item.distributor.group
            else if (item.distributor.name) {
                return item.distributor.name
            } else {
                return "" // no name to return
            }
        },

        getPriceCategory(selection, type = "text") {
            if (selection) {
            return DropdownGeneralPriceCategories.find(obj => {
                return obj.value == selection
            })[type]
        }
        return ""
        },

        addNewRow(){
            this.$store.commit('setPricelistsPricelistsRowsIsNewSearch', true) // add a new row
            this.$store.commit('setShowEditingDialog', true)
            this.$store.commit('setEditingDialogHeadline', this.$t('lang_GENERAL_CAPTION_NEW_ENTRY'))

            let defaultValues = JSON.parse(JSON.stringify(this.$store.getters.getDefaultPricelistsPricelistsRows)) 
            this.$store.commit('setPricelistsPricelistsRowsEditingDialogDetails', defaultValues)
        },

        deleteRow(item) {
            // set the selected share/row that should be deleted from the delete action 
            // and open the deletion dialog
            this.$store.commit("setSelectedPricelistsPricelistsRows", [item.id])
            this.$store.commit('setDeletionDialogContext', 'SelectedPricelistsPricelistsRows')
            this.$store.commit('setShowDeletionDialog', true)
        },

        editInlineFunction(item) {
            this.$store.commit('setSelectedPricelistsPricelistsRows', item.id)
            this.$store.commit('setPricelistsPricelistsRowsIsNewSearch', false) // editing an existing pricelists row
            let detailsDeepCopy = JSON.parse(JSON.stringify(item))

            // handling string/num types and null, formatting numbers to show in correct notation
            if (detailsDeepCopy['msp'] != null) 
                detailsDeepCopy['msp'] = this.$n(this.$alvPlugin.stringToNumber(detailsDeepCopy['msp']), 'number')
            if (detailsDeepCopy['ppd'] != null) 
                detailsDeepCopy['ppd'] = this.$n(this.$alvPlugin.stringToNumber(detailsDeepCopy['ppd']), 'number')
            if (detailsDeepCopy['srp'] != null) 
                detailsDeepCopy['srp'] = this.$n(this.$alvPlugin.stringToNumber(detailsDeepCopy['srp']), 'number')
            if (detailsDeepCopy['sps'] != null) 
                detailsDeepCopy['sps'] = this.$n(this.$alvPlugin.stringToNumber(detailsDeepCopy['sps']), 'number')

            let regionsObject   = detailsDeepCopy.regions
            if (regionsObject  != null) 
                if (regionsObject.include) {
                    detailsDeepCopy['regions'] = this.$alvPlugin.createRegionsToString(regionsObject)
                }

            // set the copy in dialog details to edit
            this.$store.commit('setPricelistsPricelistsRowsEditingDialogDetails', detailsDeepCopy)
            this.$store.commit('setEditingDialogHeadline', this.$t("lang_GENERAL_CAPTION_EDITING_ENTRY")) // set headline
            this.$store.commit('setShowEditingDialog', true) 
        }
    },

    created() {
        this.$store.dispatch('fetchDropdownPricelistsDistributor', ['vertrieb'])
    }
}
</script>